import { useState, useRef } from 'react';
import type { KeyboardEvent, ChangeEvent } from 'react';
import { X } from 'lucide-react';
import { isValidEmail } from '@/lib/email-validation';

interface PillInputProps {
  id?: string;
  value: string[];
  onChange: (pills: string[]) => void;
  placeholder?: string;
  className?: string;
  'data-testid'?: string;
}

export function PillInput({ id, value, onChange, placeholder, className, 'data-testid': testId }: PillInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const commitInput = (raw: string) => {
    const email = raw.trim().replace(/,/g, '');
    if (!email) return;
    if (!isValidEmail(email)) {
      setInputError(`"${email}" is not a valid email`);
      return;
    }
    if (!value.includes(email)) {
      onChange([...value, email]);
    }
    setInputValue('');
    setInputError('');
  };

  const removePill = (email: string) => {
    onChange(value.filter((v) => v !== email));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      commitInput(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removePill(value[value.length - 1]);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val.endsWith(',')) {
      commitInput(val.slice(0, -1));
    } else {
      setInputValue(val);
      setInputError('');
    }
  };

  const handleBlur = () => {
    commitInput(inputValue);
  };

  return (
    <div className={className}>
      <div
        className="flex flex-wrap gap-1.5 items-center min-h-[2.5rem] px-3 py-1.5 rounded-md border border-input bg-background text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 cursor-text"
        onClick={() => inputRef.current?.focus()}
        data-testid={testId}
      >
        {value.map((email) => (
          <span
            key={email}
            className="inline-flex items-center gap-1 bg-primary/10 text-primary rounded px-1.5 py-0.5 text-xs font-medium shrink-0"
            data-testid={`pill-${email}`}
          >
            {email}
            <button
              type="button"
              className="hover:text-destructive transition-colors"
              onClick={(e) => { e.stopPropagation(); removePill(email); }}
              aria-label={`Remove ${email}`}
              data-testid={`remove-pill-${email}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          type="text"
          className="flex-1 min-w-[140px] outline-none bg-transparent placeholder:text-muted-foreground"
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={value.length === 0 ? placeholder : ''}
          aria-label={placeholder}
          data-testid={testId ? `${testId}-input` : undefined}
        />
      </div>
      {inputError && (
        <p className="text-xs text-destructive mt-1" data-testid={testId ? `${testId}-error` : undefined}>{inputError}</p>
      )}
    </div>
  );
}
